// Auth types for the Vharta Platform

export interface User {
  id: string
  email: string
  name?: string
  fullName?: string // Backend returns fullName
  tenantId: string
  tenantName?: string
  accentColor?: string // User-selected tenant accent color (hex)
  role: UserRole
  /**
   * Composable approval personas this user holds on the current tenant
   * (`approval_approver`, `approval_viewer`), from
   * `tenant_users.additional_roles`. SEPARATE from `role` — a user is e.g.
   * tenant_operator AND approval_approver.
   *
   * EMPTY/ABSENT means "holds no persona", NOT "the server did not say" —
   * deliberately the opposite of `capabilities` below. A role gate must fail
   * CLOSED, and there is no static table to fall back on for a persona.
   *
   * Before this existed (RBAC sweep finding qfq/R-1), every console guard
   * naming a persona was dead code: `hasRole` compared only `role`, so the
   * approval-gates route and nav tab could never match an approval_approver
   * the SERVER already authorizes.
   */
  additionalRoles?: string[]
  permissions: string[]
  /**
   * Server-computed effective capability KEYS for this user: every base role
   * they carry (primary plus additional_roles) unioned with their custom
   * role's capabilities. From rbac.EffectiveCapabilities via the auth payload.
   *
   * ABSENT or EMPTY means "the server did not tell us", NOT "holds nothing" —
   * the resolver is nil-safe and fails soft, so useCapabilities must fall back
   * to the static role table rather than denying. See useCapabilities.ts.
   */
  capabilities?: string[]
  avatarUrl?: string
  // IANA display timezone chosen by the user, and the workspace default. Both
  // optional (unset until chosen); see utils/timezone.ts for the fallback order.
  timezone?: string
  tenantTimezone?: string
  createdAt: string
  updatedAt?: string
  // Returned by GET /api/v1/auth/me so a page load can refresh the tenant list.
  // Optional: older backends omit it, and /auth/me returns identity without it
  // if the membership lookup fails (best-effort by design).
  availableTenants?: TenantMembership[]
  // Server-authoritative indicator of how the current session was established,
  // returned by GET /api/v1/auth/me. Use this (via authStore's `authMethod`)
  // instead of decoding the JWT client-side — SSO sessions are frequently
  // cookie-only (no JS-readable token) or re-minted as local-issuer tokens
  // after a single-tenant auto-switch-tenant, so a client-side decode is
  // unreliable. Optional/absent on older backends — treat as 'local'.
  auth_method?: 'sso' | 'local'
}

export type UserRole =
  // Platform Core (4)
  | 'platform_admin'
  | 'platform_operator'
  | 'platform_viewer'
  | 'platform_auditor'
  // Platform Operations (7)
  | 'platform_sre'
  | 'platform_infra_admin'
  | 'platform_app_operator'
  | 'platform_security_operator'
  | 'platform_observer'
  | 'platform_debugger'
  | 'platform_on_call'
  // Platform Finance (4)
  | 'platform_finance_admin'
  | 'platform_finance_analyst'
  | 'platform_billing_ops'
  | 'platform_cost_engineer'
  // Platform Support (4)
  | 'platform_support_l1'
  | 'platform_support_l2'
  | 'platform_support_l3'
  | 'platform_support_manager'
  // Tenant Core (4)
  | 'tenant_owner'
  | 'tenant_admin'
  | 'tenant_operator'
  | 'tenant_viewer'
  // Tenant Finance (3)
  | 'tenant_billing_admin'
  | 'tenant_finance_viewer'
  | 'tenant_cost_center_admin'
  // Tenant Approval specialised roles (2)
  // These are composable roles assigned in addition to a base tenant role.
  // approval_approver — can decide (approve/deny) approval requests; mirrors backend OPA
  // approval_viewer   — read-only access to the approvals queue (compliance/auditor persona)
  | 'approval_approver'
  | 'approval_viewer'

// Helper function to check if a role is a platform-level role
export function isPlatformRole(role: UserRole): boolean {
  return role.startsWith('platform_')
}

/**
 * The 4 canonical tenant-core roles — mirrors the backend's
 * rbac.IsCanonicalTenantRole (tenant_viewer, tenant_operator, tenant_admin,
 * tenant_owner). Single source for any UI that needs to enumerate exactly
 * these 4, e.g. a role picker whose value is validated server-side against
 * that same canonical set (see notification-channels'
 * WebWidgetChannelConfig.agent_acting_role).
 */
export const TENANT_CORE_ROLES: {
  value: 'tenant_owner' | 'tenant_admin' | 'tenant_operator' | 'tenant_viewer'
  label: string
}[] = [
  { value: 'tenant_owner', label: 'Owner' },
  { value: 'tenant_admin', label: 'Admin' },
  { value: 'tenant_operator', label: 'Operator' },
  { value: 'tenant_viewer', label: 'Viewer' },
]

export interface LoginRequest {
  email: string
  password: string
  tenantId?: string
}

export interface TenantMembership {
  tenantId: string
  tenantName: string
  accentColor?: string // User-selected tenant accent color (hex)
  role: UserRole
  isPlatform: boolean
}

export interface LoginResponse {
  user: User
  accessToken: string // Backend uses 'accessToken', not 'token'
  refreshToken: string
  expiresIn: number // Backend sends expiresIn (seconds), not expiresAt (timestamp)
  idleTimeout?: number // Session idle window (seconds); absent on dev-mode tokens. Drives the idle-sign-out warning.
  availableTenants: TenantMembership[]
  requiresTenantSelection: boolean
}

/**
 * POST /auth/login answer when the password was right and a one-time code was
 * emailed (internal/api/v1/auth/login_otp.go). No session exists yet: finish
 * with POST /auth/login/verify-otp, which returns the normal LoginResponse.
 */
export interface LoginOTPChallenge {
  mfaRequired: true
  mfaMethod: 'email_otp'
  challengeToken: string
  maskedEmail: string
  expiresIn: number // seconds until the code expires
  resendAfter: number // seconds before another code may be requested
}

export interface ResendLoginOTPResponse {
  success: boolean
  expiresIn: number
  resendAfter: number
}

export function isLoginOTPChallenge(r: LoginResponse | LoginOTPChallenge): r is LoginOTPChallenge {
  return (r as LoginOTPChallenge).mfaRequired === true
}

export interface SwitchTenantRequest {
  tenantId: string
}

export interface SwitchTenantResponse {
  user: User
  accessToken: string
  expiresIn: number
  idleTimeout?: number // Session idle window (seconds); absent on dev-mode tokens. Drives the idle-sign-out warning.
}

export interface RegisterRequest {
  email: string
  password: string
  name: string
  tenantId: string
}

export interface RefreshTokenRequest {
  refreshToken: string
  currentTenantId?: string
}

export interface RefreshTokenResponse {
  accessToken: string // Backend uses 'accessToken', not 'token'
  refreshToken: string
  expiresIn: number // Backend sends expiresIn (seconds), not expiresAt (timestamp)
  idleTimeout?: number // Session idle window (seconds); absent on dev-mode tokens. Drives the idle-sign-out warning.
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface ChangePasswordResponse {
  success: boolean
  message: string
  /** Sessions on other devices that were signed out by the change. */
  otherSessionsRevoked?: number
  /** false: this session was signed out too (it could not be identified); sign in again. */
  currentSessionKept?: boolean
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

export interface InvitationInfoResponse {
  success: boolean
  data: {
    invitation_id: string
    tenant_id: string
    tenant_name: string
    email: string
    full_name: string
    role: string
    already_verified: boolean
    expires_at: string
  }
}

export interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<{ serverRevoked: boolean }>
  refreshSession: () => Promise<void>
}

// Permission constants
export const PERMISSIONS = {
  // Tenant permissions
  TENANT_CREATE: 'tenant:create',
  TENANT_READ: 'tenant:read',
  TENANT_UPDATE: 'tenant:update',
  TENANT_DELETE: 'tenant:delete',
  TENANT_LIST: 'tenant:list',

  // Platform user permissions
  PLATFORM_USER_CREATE: 'platform_user:create',
  PLATFORM_USER_READ: 'platform_user:read',
  PLATFORM_USER_UPDATE: 'platform_user:update',
  PLATFORM_USER_DELETE: 'platform_user:delete',
  PLATFORM_USER_LIST: 'platform_user:list',

  // Workflow permissions
  WORKFLOW_CREATE: 'workflow:create',
  WORKFLOW_READ: 'workflow:read',
  WORKFLOW_UPDATE: 'workflow:update',
  WORKFLOW_DELETE: 'workflow:delete',
  WORKFLOW_DEPLOY: 'workflow:deploy',
  WORKFLOW_EXECUTE: 'workflow:execute',

  // Execution permissions
  EXECUTION_READ: 'execution:read',
  EXECUTION_CANCEL: 'execution:cancel',
  EXECUTION_RETRY: 'execution:retry',

  // Cost permissions
  COST_READ: 'cost:read',
  // DEAD STRING — no role holds cost:manage anywhere in rbac.rego (verified by
  // an exhaustive sweep of every "cost" grant), and nothing outside this file
  // reads this constant, so the five ROLE_PERMISSIONS entries carrying it are
  // inert. Left in place rather than deleted because removing it would edit
  // five unrelated roles' entries for a provable no-op. The cost-write gates
  // that DO exist are update:budget, manage:billing and export:cost.
  COST_MANAGE: 'cost:manage',
  BUDGET_CREATE: 'budget:create',
  BUDGET_UPDATE: 'budget:update',
  BUDGET_DELETE: 'budget:delete',

  // User permissions
  USER_CREATE: 'user:create',
  USER_READ: 'user:read',
  USER_UPDATE: 'user:update',
  USER_DELETE: 'user:delete',

  // Settings permissions
  SETTINGS_READ: 'settings:read',
  SETTINGS_UPDATE: 'settings:update',

  // Agent permissions
  AGENT_CREATE: 'agent:create',
  AGENT_READ: 'agent:read',
  AGENT_UPDATE: 'agent:update',
  AGENT_DELETE: 'agent:delete',
  AGENT_EXECUTE: 'agent:execute',

  // Audit permissions
  AUDIT_READ: 'audit:read',
  AUDIT_EXPORT: 'audit:export',

  // AI permissions (tier enforcement is on the backend via OPA ai_access.rego)
  AI_CHAT: 'ai:chat',

  // Dependent-event backbone (ADR-141). MUST mirror the "event" resource in
  // internal/authz/opa/policies/rbac.rego and effective_capabilities.go:
  // owner/admin/platform_admin = read+publish+subscribe; operator = read+publish;
  // viewer = read. The console gates buttons on these capabilities, never on
  // role lists, so the two cannot drift silently.
  EVENT_READ: 'event:read',
  EVENT_PUBLISH: 'event:publish',
  EVENT_SUBSCRIBE: 'event:subscribe',
} as const

// Role permission mappings
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  // ============================================
  // Platform Core roles
  // ============================================
  platform_admin: Object.values(PERMISSIONS),
  platform_operator: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.PLATFORM_USER_CREATE,
    PERMISSIONS.PLATFORM_USER_READ,
    PERMISSIONS.PLATFORM_USER_UPDATE,
    PERMISSIONS.PLATFORM_USER_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_viewer: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.PLATFORM_USER_READ,
    PERMISSIONS.PLATFORM_USER_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  // platform_auditor — read-only access to audit data across all tenants (ADR-0040)
  // CANNOT write, approve, or execute anything.
  platform_auditor: [PERMISSIONS.AUDIT_READ, PERMISSIONS.AUDIT_EXPORT],

  // ============================================
  // Platform Operations roles
  // ============================================
  platform_sre: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_infra_admin: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_app_operator: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.WORKFLOW_DEPLOY,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_security_operator: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_observer: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_debugger: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_on_call: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
  ],

  // ============================================
  // Platform Finance roles
  // ============================================
  platform_finance_admin: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.COST_READ,
    PERMISSIONS.COST_MANAGE,
    PERMISSIONS.BUDGET_CREATE,
    PERMISSIONS.BUDGET_UPDATE,
    PERMISSIONS.BUDGET_DELETE,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_finance_analyst: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_billing_ops: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.COST_READ,
    PERMISSIONS.COST_MANAGE,
    PERMISSIONS.BUDGET_CREATE,
    PERMISSIONS.BUDGET_UPDATE,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_cost_engineer: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.COST_READ,
    PERMISSIONS.COST_MANAGE,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],

  // ============================================
  // Platform Support roles
  // ============================================
  platform_support_l1: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.SETTINGS_READ,
  ],
  platform_support_l2: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
  ],
  platform_support_l3: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.USER_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],
  platform_support_manager: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_LIST,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.USER_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
    // audit:read — the golden grants it to this role; the table withheld it,
    // so canViewAudit was false and /platform/audit was hidden (14 roles,
    // the KNOWN_HIDDEN list in navigation.rbac.test.tsx).
    PERMISSIONS.AUDIT_READ,
  ],

  // ============================================
  // Tenant Core roles
  // ============================================
  tenant_owner: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_UPDATE,
    PERMISSIONS.WORKFLOW_CREATE,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.WORKFLOW_UPDATE,
    PERMISSIONS.WORKFLOW_DELETE,
    PERMISSIONS.WORKFLOW_DEPLOY,
    PERMISSIONS.WORKFLOW_EXECUTE,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.EXECUTION_RETRY,
    PERMISSIONS.COST_READ,
    PERMISSIONS.COST_MANAGE,
    PERMISSIONS.BUDGET_CREATE,
    PERMISSIONS.BUDGET_UPDATE,
    PERMISSIONS.BUDGET_DELETE,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
    PERMISSIONS.AGENT_CREATE,
    PERMISSIONS.AGENT_READ,
    PERMISSIONS.AGENT_UPDATE,
    PERMISSIONS.AGENT_DELETE,
    PERMISSIONS.AGENT_EXECUTE,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.AUDIT_EXPORT,
    PERMISSIONS.AI_CHAT,
    PERMISSIONS.EVENT_READ,
    PERMISSIONS.EVENT_PUBLISH,
    PERMISSIONS.EVENT_SUBSCRIBE,
  ],
  tenant_admin: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_UPDATE,
    PERMISSIONS.WORKFLOW_CREATE,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.WORKFLOW_UPDATE,
    PERMISSIONS.WORKFLOW_DELETE,
    PERMISSIONS.WORKFLOW_DEPLOY,
    PERMISSIONS.WORKFLOW_EXECUTE,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.EXECUTION_RETRY,
    PERMISSIONS.COST_READ,
    PERMISSIONS.COST_MANAGE,
    PERMISSIONS.BUDGET_CREATE,
    PERMISSIONS.BUDGET_UPDATE,
    PERMISSIONS.BUDGET_DELETE,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
    PERMISSIONS.AGENT_CREATE,
    PERMISSIONS.AGENT_READ,
    PERMISSIONS.AGENT_UPDATE,
    PERMISSIONS.AGENT_DELETE,
    PERMISSIONS.AGENT_EXECUTE,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.AI_CHAT,
    PERMISSIONS.EVENT_READ,
    PERMISSIONS.EVENT_PUBLISH,
    PERMISSIONS.EVENT_SUBSCRIBE,
  ],
  tenant_operator: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.WORKFLOW_CREATE,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.WORKFLOW_UPDATE,
    PERMISSIONS.WORKFLOW_DEPLOY,
    PERMISSIONS.WORKFLOW_EXECUTE,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.EXECUTION_CANCEL,
    PERMISSIONS.EXECUTION_RETRY,
    PERMISSIONS.COST_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.AGENT_READ,
    PERMISSIONS.AGENT_EXECUTE,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.AI_CHAT,
    PERMISSIONS.EVENT_READ,
    PERMISSIONS.EVENT_PUBLISH,
  ],
  tenant_viewer: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.WORKFLOW_READ,
    PERMISSIONS.EXECUTION_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.AGENT_READ,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.EVENT_READ,
  ],

  // ============================================
  // Tenant Finance roles
  // ============================================
  // Kept a SUBSET of rbac.rego's grant for this role so the UI never offers a
  // control the API will refuse. Removed: COST_MANAGE (no role holds
  // cost:manage anywhere in the policy), USER_READ and SETTINGS_READ (the
  // backend grants this role neither "user" nor "settings").
  tenant_billing_admin: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.BUDGET_CREATE,
    PERMISSIONS.BUDGET_UPDATE,
    PERMISSIONS.BUDGET_DELETE,
  ],
  // Read-only finance persona. USER_READ / SETTINGS_READ removed — not granted
  // by rbac.rego, so they only produced controls that 403.
  tenant_finance_viewer: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.COST_READ,
  ],
  // READ-ONLY. The design doc scopes this role to its own cost centre(s), and a
  // BASE role cannot be narrowed (abac_governed_types governs the custom-role
  // path only), so rbac.rego grants it reads and no writes. Its cost/budget/
  // alert reads sit at tenant_viewer's tier; cost_center does NOT -- tenant_viewer
  // has no cost_center grant at all, so that cell is this role's own. Parity with
  // tenant_viewer is NOT the same as "no exposure": 1.2 says this role "cannot see
  // other departments" and it can read every department's. See rbac.rego.
  // BUDGET_CREATE/BUDGET_UPDATE were the dead controls this whole fix
  // exists to remove: the UI offered Create/Edit Budget and every click 403'd.
  tenant_cost_center_admin: [
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.COST_READ,
  ],

  // ============================================
  // Tenant Approval specialised roles
  // Composable: assigned alongside a base tenant role (e.g. tenant_viewer + approval_approver).
  // ============================================
  // approval_approver — can read and decide (approve/deny) approval requests.
  // Mirrors the backend OPA RequiredRoles for approval.decide.
  approval_approver: [PERMISSIONS.TENANT_READ, PERMISSIONS.SETTINGS_READ],
  // approval_viewer — read-only access to the approvals queue (compliance/auditor persona).
  // Mirrors the backend OPA RequiredRoles for approval.read / approval.list.
  approval_viewer: [PERMISSIONS.TENANT_READ, PERMISSIONS.SETTINGS_READ],
}

/**
 * Every platform-plane role, DERIVED — never hand-listed.
 *
 * App.tsx used to carry its own literal
 * ['platform_admin','platform_operator','platform_viewer','platform_auditor']
 * to decide which plane a user routes to, while usePersona decides isPlatform
 * via isPlatformRole() (`startsWith('platform_')`, i.e. all 19). The two
 * disagreed for the other 15 platform roles — the 7 operations, 4 support and
 * 4 finance ones — and since the sidebar follows usePersona while the routing
 * followed that literal, those roles were shown a platform sidebar in which
 * EVERY link bounced back to /tenant/dashboard, on a tenant dashboard whose own
 * nav was empty (all tenant nav items require p.isTenant). /platform/
 * impersonation was unreachable by exactly the platform_support_* roles it is
 * written for.
 *
 * ROLE_PERMISSIONS is Record<UserRole, string[]>, so the compiler forces it to
 * list every role; filtering it through the same isPlatformRole() the persona
 * hook uses makes a future divergence impossible rather than merely fixed once.
 *
 * Deliberately defined HERE rather than in App.tsx: a first version derived it
 * from usePersona's ROLE_LABELS, which made App.tsx throw at import time in
 * every test that mocks that hook (ROLE_LABELS came back undefined). This
 * module holds the role type itself and is not mocked.
 */
export const PLATFORM_ROLES: UserRole[] = (Object.keys(ROLE_PERMISSIONS) as UserRole[]).filter(
  isPlatformRole,
)

/**
 * The composable approval personas, as a CLOSED SET.
 *
 * This mirrors three server-side authorities that all agree:
 *   - the DB CHECK in migration 000559:
 *     `additional_roles <@ ARRAY['approval_approver','approval_viewer']`
 *   - `rbac.IsValidSpecialistRole`, enforced on the write path
 *     (user/handler.go)
 *   - the JWT gate at middleware/jwt.go, which FILTERS incoming `aroles`
 *     against that same function because "if the signing key were ever
 *     compromised an attacker could forge aroles with a primary/platform
 *     role".
 *
 * The client applies the same allowlist for the same reason. Without it,
 * `hasRole` would accept ANY string the server put in additionalRoles as a
 * role — including `tenant_owner` — which would make the browser a second,
 * looser authority than the gate that already rejects exactly that. The
 * consequence is UI-only (a client-side attacker can edit persisted state
 * anyway), but being asymmetric with the server's own filter is the defect.
 */
export const SPECIALIST_ROLES = ['approval_approver', 'approval_viewer'] as const

export type SpecialistRole = (typeof SPECIALIST_ROLES)[number]

/**
 * specialistRolesOf narrows an untrusted string list to the personas above,
 * dropping anything else. Use it at every boundary that reads
 * `additionalRoles`, so no consumer has to remember the allowlist.
 */
export function specialistRolesOf(values: readonly string[] | undefined): SpecialistRole[] {
  if (!values) return []
  return values.filter((v): v is SpecialistRole =>
    (SPECIALIST_ROLES as readonly string[]).includes(v),
  )
}
